import { AppIssueForm } from "./AppIssueForm";
import { completionFeedback } from "../ui/feedback";
import { useEffect, useRef, useState } from "react";
import type { TripWorkspace } from "@japangolmok/core";
import { getTrip, saveTrip } from "../storage/trips";
import { BrandMark } from "../ui/BrandMark";
import { Layout } from "../ui/Shell";

export type TripWorkspaceState =
	| { status: "loading" }
	| { status: "ready"; trip: TripWorkspace }
	| { status: "not_found" }
	| { status: "storage_error" };

type TripReader = (id: string) => Promise<TripWorkspace | undefined>;
type TripWriter = (trip: TripWorkspace) => Promise<void>;

export function LoadingTrip({
	label = "여행을 불러오는 중",
}: {
	label?: string;
}) {
	return (
		<Layout>
			<section className="section">
				<div className="skeleton-list" role="status" aria-label={label} />
			</section>
		</Layout>
	);
}

export function MissingTrip() {
	return (
		<Layout>
			<section className="empty-state page-not-found">
				<BrandMark size={64} />
				<h1>이 기기에서 여행을 찾지 못했어요.</h1>
				<p>
					여행은 계정 없이 이 기기에 저장돼요. 받아 둔 여행 백업 파일이 있다면
					다시 가져올 수 있어요.
				</p>
				<div className="button-row">
					<a className="button primary" href="/trips">
						내 여행 보기
					</a>
					<a className="button secondary" href="/trips/new">
						새 여행 만들기
					</a>
				</div>
			</section>
		</Layout>
	);
}

export function TripStorageError({ retry }: { retry: () => void }) {
	return (
		<Layout>
			<section className="empty-state page-not-found" role="alert">
				<BrandMark size={64} />
				<h1>이 기기의 여행 저장소를 읽지 못했어요.</h1>
				<p>
					여행이 없다고 처리하지 않았어요. 브라우저의 사이트 데이터 허용 여부와
					남은 저장 공간을 확인한 뒤 다시 읽어 주세요. 다른 탭이 열려 있다면
					다른 탭의 일본골목을 닫고 다시 열어 주세요.
				</p>
				<div className="button-row">
					<button className="button primary" type="button" onClick={retry}>
						여행 다시 읽기
					</button>
					<a className="button secondary" href="/trips">
						내 여행에서 백업 가져오기
					</a>
				</div>
				<AppIssueForm />
			</section>
		</Layout>
	);
}

export function useTripWorkspace(
	id: string,
	readTrip: TripReader = getTrip,
	writeTrip: TripWriter = saveTrip,
) {
	const [state, setState] = useState<TripWorkspaceState>({ status: "loading" });
	const [attempt, setAttempt] = useState(0);
	const [saveError, setSaveError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);
	const undoRef = useRef<{ trip: TripWorkspace; expires: number } | null>(null);
	const [canUndo, setCanUndo] = useState(false);
	const confirmed = useRef<TripWorkspace | null>(null);
	const queue = useRef<Promise<unknown>>(Promise.resolve());
	const generation = useRef(0);
	const pending = useRef(0);
	useEffect(() => {
		const token = ++generation.current;
		confirmed.current = null;
		setState({ status: "loading" });
		setSaveError(null);
		setSaving(false);
		readTrip(id)
			.then((trip) => {
				if (token !== generation.current) return;
				confirmed.current = trip ?? null;
				setState(trip ? { status: "ready", trip } : { status: "not_found" });
			})
			.catch(() => {
				if (token === generation.current) setState({ status: "storage_error" });
			});
		return () => {
			generation.current += 1;
		};
	}, [id, attempt, readTrip]);
	const persist = (
		change: (draft: TripWorkspace) => void,
	): Promise<boolean> => {
		const token = generation.current;
		pending.current += 1;
		setSaving(true);
		const operation = queue.current.then(async () => {
			try {
				if (token !== generation.current || !confirmed.current) return false;
				// Read the last committed revision when this operation starts, not when clicked.
				const next = structuredClone(confirmed.current);
				change(next);
				next.updatedAt = new Date().toISOString();
				await writeTrip(next);
				if (token !== generation.current) return false;
				if (
					next.days.reduce((n, d) => n + d.items.length, 0) <
						confirmed.current.days.reduce((n, d) => n + d.items.length, 0) ||
					next.expenses.length < confirmed.current.expenses.length
				) {
					undoRef.current = {
						trip: structuredClone(confirmed.current),
						expires: Date.now() + 10000,
					};
					setCanUndo(true);
					window.setTimeout(() => setCanUndo(false), 10000);
				}
				confirmed.current = next;
				setState({ status: "ready", trip: next });
				setSaveError(null);
				completionFeedback();
				return true;
			} catch (cause) {
				if (token === generation.current)
					setSaveError(
						cause instanceof Error &&
							["STALE_DAY_ADJUSTMENT", "STALE_STOP_REPLACEMENT"].includes(
								cause.message,
							)
							? "그동안 일정이 달라졌어요. 변경안을 다시 확인해 주세요."
							: "변경을 저장하지 못했어요. 마지막으로 저장한 여행과 입력은 그대로예요. 저장 공간을 확인한 뒤 같은 작업을 다시 시도해 주세요.",
					);
				return false;
			} finally {
				pending.current -= 1;
				if (token === generation.current) setSaving(pending.current > 0);
			}
		});
		queue.current = operation;
		return operation;
	};
	const undoDeletion = async () => {
		const row = undoRef.current;
		if (!row || row.expires < Date.now()) return;
		const ok = await persist((draft) => {
			const present = new Set(
				[
					...draft.days.flatMap((d) => d.items),
					...(draft.candidates ?? []),
				].map((i) => i.id),
			);
			const restored = new Set<string>();
			for (const oldDay of row.trip.days) {
				const target = draft.days.find((d) => d.date === oldDay.date);
				oldDay.items.forEach((item, index) => {
					if (present.has(item.id)) return;
					restored.add(item.id);
					if (target)
						target.items.splice(
							Math.min(index, target.items.length),
							0,
							structuredClone(item),
						);
					else
						draft.candidates = [
							...(draft.candidates ?? []),
							structuredClone(item),
						];
				});
			}
			for (const item of row.trip.expenses)
				if (!draft.expenses.some((e) => e.id === item.id))
					draft.expenses.push(structuredClone(item));
			draft.filmMoments?.forEach((moment) => {
				const old = row.trip.filmMoments?.find((m) => m.id === moment.id);
				if (
					old?.planItemId &&
					restored.has(old.planItemId) &&
					draft.days.some(
						(d) =>
							d.date === old.dayDate &&
							d.items.some((i) => i.id === old.planItemId),
					)
				) {
					moment.planItemId = old.planItemId;
					moment.dayDate = old.dayDate;
				}
			});
			draft.lastAdjustment = null;
			draft.lastReplacement = null;
		});
		if (ok) {
			undoRef.current = null;
			setCanUndo(false);
		}
	};
	return {
		canUndo,
		undoDeletion,
		state,
		persist,
		retry: () => setAttempt((value) => value + 1),
		saveError,
		saving,
	};
}
